/**
 * The tool catalog `carrier_ask` routes against — shared by both MCP surfaces.
 *
 * WHY THIS IS SHARED
 *   `carrier_ask` exists twice: apps/mcp-server (the hosted Worker) and
 *   apps/mcp-stdio (the published CLI, which is what `carrier ask` actually
 *   runs). Fixing the Worker's router in #731 did nothing for the CLI, because
 *   the CLI holds its own 430-line hardcoded catalog. Same lesson as
 *   list-subscriber.ts and ocs-param-shapes.ts: the fix has to live in one
 *   place or it only ever lands on one surface.
 *
 * WHY THE ROUTER WAS WRONG
 *   It chose from short hand-written hints and, on the Worker, a generated stub
 *   for anything not hand-written:
 *
 *     "Carrier MCP tool `high_cost_subscribers` (high cost subscribers).
 *      Required scope: read. Use when the user intent clearly matches this
 *      tool name or its domain."
 *
 *   That is name matching. Measured against the live fleet:
 *
 *     "what is the total cost of the mbs of my users"
 *       -> high_cost_subscribers   (a >80%-utilisation report, not a total)
 *     "which reseller sits directly above Lifecycle Innovations"
 *       -> list_reseller_accounts  (answer is get_reseller_info.parentName)
 *     "what is the VoIP charging plan name for this reseller"
 *       -> a tool returning a bare {status: OK}
 *
 *   Every one returned real data about a different question, which is worse
 *   than returning nothing: it reads as an answer.
 *
 *   Meanwhile the tools carry descriptions written for exactly this job, and 70
 *   of 123 carry an explicit "Do NOT use this to X — use Y instead" naming the
 *   tool that IS right. The router never saw them.
 */

/** Per-process registry of the descriptions tools registered with. */
const descriptions = new Map<string, string>();

/** Called from each app's registerTool wrapper. */
export function recordToolDescription(name: string, description: string): void {
  if (description) descriptions.set(name, description);
}

export function getToolDescription(name: string): string | undefined {
  return descriptions.get(name);
}

export function recordedToolCount(): number {
  return descriptions.size;
}

/** Test seam — never called in production. */
export function resetToolDescriptions(): void {
  descriptions.clear();
}

/**
 * Trim a description to what the router needs to choose correctly.
 *
 * All 123 full descriptions are ~63 KB (~15.8k tokens) per routing call. Most
 * of each is parameter and return detail the router does not use — it picks a
 * tool, it does not call one. What it does need is the opening statement of
 * purpose and, above all, the "Do NOT" steers, since those are exactly the
 * near-miss pairs it was confusing.
 *
 * Keeps the first two sentences plus every sentence containing a Do NOT steer.
 * Splitting on ". " followed by a capital keeps "e.g." and decimals intact.
 */
export function routerDescription(full: string | undefined): string | undefined {
  if (!full) return undefined;

  const sentences = full
    .split(/(?<=\.)\s+(?=[A-Z`'"])/)
    .map((s) => s.trim())
    .filter(Boolean);

  const kept = [
    ...sentences.slice(0, 2),
    ...sentences.slice(2).filter((s) => /\bDo NOT\b/i.test(s)),
  ];

  return kept.length ? kept.join(" ") : full;
}

export interface RouterTool {
  name: string;
  description: string;
  input_schema: { type: "object"; properties: Record<string, unknown>; required?: string[] };
}

export interface BuildRouterCatalogOptions {
  /** Every routable tool name. */
  toolNames: Iterable<string>;
  /** Hand-written entries: richer input hints, and a fallback description. */
  curated: readonly RouterTool[];
  /** The clarify sentinel, always offered first. */
  clarifyTool: RouterTool;
  /** Scope lookup, used only for the last-resort stub text. */
  getScope: (name: string) => string;
}

/**
 * Build the catalog, preferring each tool's OWN registered description.
 *
 * Order of preference per tool:
 *   1. the recorded description, trimmed — what the tool actually does
 *   2. the curated hand-written hint — for tools not registered on this
 *      surface (docs-only entries such as stripe_proxy_call)
 *   3. a name-only stub — last resort
 *
 * (2) and (3) used to be the only sources. Curated input_schema hints are kept
 * either way: they tell the router which params are worth extracting.
 *
 * Call this per route, not at module scope — descriptions are only populated
 * once tools have registered.
 */
export function buildRouterCatalog(opts: BuildRouterCatalogOptions): RouterTool[] {
  const curatedByName = new Map(opts.curated.map((t) => [t.name, t]));
  const tools: RouterTool[] = [];

  for (const name of [...opts.toolNames].sort()) {
    if (name === "carrier_ask" || name === "carrier_clarify") continue;

    const curated = curatedByName.get(name);
    const real = routerDescription(getToolDescription(name));

    tools.push({
      name,
      description:
        real ??
        curated?.description ??
        `Carrier MCP tool \`${name}\`. Required scope: ${opts.getScope(name)}. ` +
          `Use when the user intent clearly matches this tool name or its domain.`,
      input_schema: curated?.input_schema ?? { type: "object", properties: {} },
    });
  }

  return [opts.clarifyTool, ...tools];
}

/**
 * Shared router rules.
 *
 * The original prompt had no way to decline: rule 1 said pick the best match
 * and clarify was discouraged, so a near-miss always won over saying nothing.
 * Rules 7-9 are the corrective, and they are the reason the three live failures
 * above should now come back as clarify rather than as confident wrong data.
 */
export const ROUTER_RULES = `Rules:
1. Pick the single best-matching tool. Never pick carrier_ask (the router itself).
2. Extract any parameters mentioned in the intent (ICCID, account IDs, amounts, etc.) as the tool's input.
3. Use carrier_clarify when the intent is ambiguous between several tools, AND when no tool actually answers the question. Returning nothing useful is correct and expected; a near-miss is not.
4. DESTRUCTIVE tools are flagged in their descriptions — still pick them if they match; the safety layer handles the confirm flow.
5. Only include params explicitly mentioned in the intent.
6. Context fields (iccid, account_id, reseller_id) from the routing context take precedence.
7. A tool answers the question only if its description says it does. Do not infer capability from the tool's NAME: several names share words with unrelated questions, and picking on the name alone has produced confidently wrong answers.
8. Read the "Do NOT use this to …" steers in a description as hard exclusions. They exist because that tool is the common wrong answer for a neighbouring question, and they name the tool to use instead.
9. Prefer carrier_clarify over a tool that would return real data about a different question. Answering "which subscribers erode margin" when the user asked "what do my users' megabytes cost in total" is worse than admitting the gap, because the output looks like an answer.`;
